import React, {useContext, useEffect, useState} from "react";
import {OrganizationServiceContext, SbomProjectGroupServiceContext} from "../../Contexts";
import Organization from "../../domain/Organization";
import SbomProjectGroup from "../../domain/SbomProjectGroup";
import QueryOptions from "../../sal-ui/QueryOptions";
import {FilterItem, FilterItemProps, InputEditor, SelectEditor} from "../tablefilter/TableFilter";
import {SearchFilter} from "../tablefilter/SearchFilter";
import {FindingSeverity} from "../../domain/FindingSeverity";

interface OrganizationItemProps extends FilterItemProps {
    organizations?: Organization[];
}

interface GroupItemProps extends FilterItemProps {
    groups?: SbomProjectGroup[];
}

export function createDefaultSbomProjectFilterValues() {
    return {name: "", severity: [], groupIds: []};
}

function NameItem(props: FilterItemProps) {
    return (
        <InputEditor
            value={props.value}
            onChange={props.onChange}
        />
    );
}

function SeverityItem(props: FilterItemProps) {
    return (
        <SelectEditor
            options={Object.keys(FindingSeverity).map(severity => ({
                value: severity,
                label: <span style={{display: "inline-block"}} className={"selection-item-text"}>&gt;= {severity}</span>
            }))}
            multiple={false}
            value={props.value}
            onChange={props.onChange}
        />
    );
}

function OrganizationItem(props: OrganizationItemProps) {
    return (
        <SelectEditor
            width={180}
            multiple={true}
            showSearch={true}
            optionFilterProp="label"
            options={props.organizations?.map(organization => ({
                value: organization.id,
                label: organization.name
            }))}
            value={props.value}
            onChange={props.onChange}
        />
    );
}

function GroupItem(props: GroupItemProps) {
    return (
        <SelectEditor
            width={220}
            multiple={true}
            showSearch={true}
            optionFilterProp="label"
            options={props.groups?.map(group => ({
                value: group.id,
                label: group.name
            }))}
            value={props.value}
            onChange={props.onChange}
        />
    );
}

interface SbomProjectFilterProps {
    className?: string;
    values?: any;
    onChange: (values: any) => void;
    showOrganizationFilter: boolean;
    showGroupFilter: boolean;
    persistentStateIdent?: string;
}

function SbomProjectFilter(props: SbomProjectFilterProps) {
    const organizationService = useContext(OrganizationServiceContext);
    const sbomProjectGroupService = useContext(SbomProjectGroupServiceContext);
    const [organizations, setOrganizations] = useState<Organization[]>();
    const [groups, setGroups] = useState<SbomProjectGroup[]>();

    useEffect(() => {
        if (!props.showOrganizationFilter) {
            return;
        }

        organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setOrganizations(result.data));
    }, [organizationService, props.showOrganizationFilter]);

    useEffect(() => {
        if (!props.showGroupFilter) {
            return;
        }

        sbomProjectGroupService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setGroups(result.data));
    }, [sbomProjectGroupService, props.showGroupFilter]);

    const items: FilterItem[] = [
        {
            name: "name",
            key: "name",
            label: "Name",
            render: itemProps => <NameItem {...itemProps}/>
        },
        {
            name: "severity",
            key: "severity",
            label: "Finding severity",
            render: itemProps => <SeverityItem {...itemProps}/>
        }
    ];

    if (props.showGroupFilter) {
        items.push({
            name: "groupIds",
            key: "groupIds",
            label: "Group",
            render: itemProps => <GroupItem {...itemProps} groups={groups}/>
        });
    }

    if (props.showOrganizationFilter) {
        items.push({
            name: "organizationIds",
            key: "organizationIds",
            label: "Organization",
            render: itemProps => <OrganizationItem {...itemProps} organizations={organizations}/>
        });
    }

    return (
        <SearchFilter
            items={items}
            className={props.className}
            values={props.values}
            defaultValues={createDefaultSbomProjectFilterValues()}
            persistentStateIdent={props.persistentStateIdent}
            defaultActiveFilterNames={["name", "groupIds", "severity"]}
            onChange={props.onChange}
        />
    );
}

export default SbomProjectFilter;
